import { ADMIN_USER_ERRORS } from '@/features/admin-users/errors/admin-user-error-messages';
import { COUNTRY_ERRORS } from '@/features/countries/errors/country-error-messages';
import { PLAN_ERRORS } from '@/features/plans/errors/plan-error-messages';
import { REGION_ERRORS } from '@/features/regions/errors/region-error-messages';
import { SOURCE_ERRORS } from '@/features/sources/errors/source-error-messages';

export interface ErrorMessage {
  title: string;
  message: string;
  severity?: 'error' | 'warning' | 'info';
}

// Mensajes de errores HTTP estándar
const HTTP_ERRORS: Record<number, ErrorMessage> = {
  400: {
    title: 'Solicitud inválida',
    message: 'Los datos enviados no son válidos. Por favor, verifica la información.',
    severity: 'error',
  },
  401: {
    title: 'No autorizado',
    message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
    severity: 'warning',
  },
  403: {
    title: 'Acceso denegado',
    message: 'No tienes permisos para realizar esta acción.',
    severity: 'error',
  },
  404: {
    title: 'No encontrado',
    message: 'El recurso solicitado no existe.',
    severity: 'error',
  },
  409: {
    title: 'Conflicto',
    message: 'Ya existe un registro con estos datos.',
    severity: 'warning',
  },
  422: {
    title: 'Error de validación',
    message: 'Los datos proporcionados no cumplen con los requisitos.',
    severity: 'error',
  },
  429: {
    title: 'Demasiadas solicitudes',
    message: 'Has excedido el límite de solicitudes. Intenta nuevamente en unos minutos.',
    severity: 'warning',
  },
  500: {
    title: 'Error del servidor',
    message: 'Ocurrió un error en el servidor. Por favor, intenta más tarde.',
    severity: 'error',
  },
  502: {
    title: 'Servicio no disponible',
    message: 'El servicio no está disponible temporalmente.',
    severity: 'error',
  },
  503: {
    title: 'Servicio en mantenimiento',
    message: 'El servicio está en mantenimiento. Intenta más tarde.',
    severity: 'warning',
  },
};

const API_ERRORS: Record<string, ErrorMessage> = {
  ...ADMIN_USER_ERRORS,
  ...COUNTRY_ERRORS,
  ...PLAN_ERRORS,
  ...REGION_ERRORS,
  ...SOURCE_ERRORS,
};

// Error por defecto
const DEFAULT_ERROR: ErrorMessage = {
  title: 'Error inesperado',
  message: 'Ocurrió un error inesperado. Por favor, intenta nuevamente.',
  severity: 'error',
};

// Error de red
const NETWORK_ERROR: ErrorMessage = {
  title: 'Error de conexión',
  message: 'No se pudo conectar con el servidor. Verifica tu conexión a internet.',
  severity: 'error',
};

// Error de timeout
const TIMEOUT_ERROR: ErrorMessage = {
  title: 'Tiempo de espera agotado',
  message: 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.',
  severity: 'warning',
};

/**
 * Obtiene el mensaje de error apropiado basado en el código de error o status HTTP
 */
export function getErrorMessage(
  errorCode?: string,
  statusCode?: number,
  detail?: string,
): ErrorMessage {
  // Prioridad 1: Código de error específico de la API
  if (errorCode && API_ERRORS[errorCode]) {
    return API_ERRORS[errorCode];
  }

  // Prioridad 2: Status HTTP
  if (statusCode && HTTP_ERRORS[statusCode]) {
    const httpError = HTTP_ERRORS[statusCode];
    // Si hay un detalle de la API, lo agregamos al mensaje
    // if (detail) {
    //   return {
    //     ...httpError,
    //     message: `${httpError.message}\nDetalle: ${detail}`,
    //   };
    // }
    return httpError;
  }

  // Prioridad 3: Mensaje por defecto con detalle si existe
  if (detail) {
    return {
      ...DEFAULT_ERROR,
      message: detail,
    };
  }

  return DEFAULT_ERROR;
}

/**
 * Obtiene el mensaje para errores de red
 */
export function getNetworkErrorMessage(): ErrorMessage {
  return NETWORK_ERROR;
}

/**
 * Obtiene el mensaje para errores de timeout
 */
export function getTimeoutErrorMessage(): ErrorMessage {
  return TIMEOUT_ERROR;
}

/**
 * Agrega un nuevo mensaje de error personalizado
 */
export function addCustomErrorMessage(code: string, message: ErrorMessage): void {
  API_ERRORS[code] = message;
}

/**
 * Extracts error info from an unknown Axios error and returns the mapped ErrorMessage.
 * Use in React Query onError callbacks to avoid repeating the extraction boilerplate.
 */
export function getErrorFromAxios(error: unknown): ErrorMessage {
  const axiosError = error as {
    response?: { data?: { title?: string; detail?: string }; status?: number };
  };
  return getErrorMessage(
    axiosError.response?.data?.title,
    axiosError.response?.status,
    axiosError.response?.data?.detail,
  );
}

export { HTTP_ERRORS, API_ERRORS, DEFAULT_ERROR, NETWORK_ERROR, TIMEOUT_ERROR };
